import { useMemo, useState } from "react";
import { Search, X } from "@/lib/icons";
import AppHeader from "@/components/AppHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { BallStickViewer } from "@/components/organic/BallStickViewer";
import { SkeletalViewer } from "@/components/organic/SkeletalViewer";
import {
  MOLECULE_LIBRARY,
  type LibraryMolecule,
  type MoleculeCategory,
} from "@/data/moleculeLibrary";
import Main from "@/components/Main";
import Seo from "@/components/Seo";

const CATEGORY_ORDER: MoleculeCategory[] = [
  "Hydrocarbons",
  "Inorganic",
  "Functional groups",
  "Biomolecules",
];

export default function MoleculeViewer() {
  const [selectedId, setSelectedId] = useState<string>(MOLECULE_LIBRARY[0].id);
  const [query, setQuery] = useState("");

  const selected = useMemo<LibraryMolecule>(
    () => MOLECULE_LIBRARY.find((m) => m.id === selectedId) ?? MOLECULE_LIBRARY[0],
    [selectedId],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return MOLECULE_LIBRARY;
    return MOLECULE_LIBRARY.filter(
      (m) =>
        m.name.toLowerCase().includes(q) ||
        m.formula.toLowerCase().includes(q) ||
        m.category.toLowerCase().includes(q),
    );
  }, [query]);

  const grouped = useMemo(() => {
    const map = new Map<MoleculeCategory, LibraryMolecule[]>();
    for (const cat of CATEGORY_ORDER) map.set(cat, []);
    for (const m of filtered) map.get(m.category)?.push(m);
    return map;
  }, [filtered]);

  return (
    <div className="min-h-screen bg-transparent">
      <Seo title="3D Molecule Viewer — ElementPad" description="View common molecules in 3D ball-and-stick and skeletal form. Rotate, zoom, and explore molecular geometry." path="/tools/molecule-viewer" />
      <AppHeader />
      <Main className="max-w-[1200px] mx-auto px-4 py-6">
        <header className="mb-5">
          <h1 className="text-2xl font-bold text-foreground mb-1">Molecule Viewer</h1>
          <p className="text-sm text-muted-foreground">
            Explore essential molecules in 2D skeletal and 3D ball-and-stick form. Drag the 3D
            view to rotate; scroll to zoom.
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-5">
          {/* Library sidebar */}
          <aside className="space-y-3" aria-label="Molecule library">
            <div className="relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
                aria-hidden="true"
              />
              <label htmlFor="mol-search" className="sr-only">
                Search molecules
              </label>
              <input
                id="mol-search"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                className="w-full h-9 pl-9 pr-9 rounded-md border border-input bg-background text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-muted-foreground hover:text-foreground"
                >
                  <X size={14} aria-hidden="true" />
                </button>
              )}
            </div>

            <div className="border border-border rounded-lg bg-card overflow-hidden">
              {CATEGORY_ORDER.map((cat) => {
                const items = grouped.get(cat) ?? [];
                if (items.length === 0) return null;
                return (
                  <div key={cat} className="border-b border-border last:border-b-0">
                    <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40">
                      {cat}
                    </div>
                    <ul role="listbox" aria-label={cat}>
                      {items.map((m) => {
                        const active = m.id === selectedId;
                        return (
                          <li key={m.id}>
                            <button
                              type="button"
                              onClick={() => setSelectedId(m.id)}
                              role="option"
                              aria-selected={active}
                              className={`w-full text-left px-3 py-2 flex items-baseline justify-between gap-2 transition-colors ${
                                active
                                  ? "bg-accent text-foreground"
                                  : "text-foreground hover:bg-accent/60"
                              }`}
                            >
                              <span className="text-sm font-medium leading-tight truncate">
                                {m.name}
                              </span>
                              <span className="text-[11px] font-mono text-muted-foreground flex-shrink-0">
                                {m.formula}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <p className="px-3 py-4 text-xs text-muted-foreground italic">
                  No molecules match "{query}".
                </p>
              )}
            </div>
          </aside>

          {/* Viewer */}
          <section aria-live="polite" aria-atomic="true">
            <div className="border border-border rounded-lg bg-card p-4">
              <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
                <div>
                  <h2 className="text-lg font-semibold text-foreground leading-tight">
                    {selected.name}
                  </h2>
                  <p className="text-xs text-muted-foreground mt-0.5">{selected.blurb}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="font-mono">
                    {selected.formula}
                  </Badge>
                  <Badge variant="outline" className="text-[10px]">
                    {selected.geometry}
                  </Badge>
                </div>
              </div>

              <Tabs defaultValue="3d" className="w-full">
                <TabsList className="mb-3">
                  <TabsTrigger value="3d">3D ball-and-stick</TabsTrigger>
                  <TabsTrigger value="2d">2D skeletal</TabsTrigger>
                </TabsList>

                <TabsContent value="3d" className="mt-0">
                  <BallStickViewer
                    key={`3d-${selected.id}`}
                    molecule={selected.view3d}
                    height={420}
                    ariaLabel={`3D ball-and-stick model of ${selected.name} (${selected.formula})`}
                  />
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    Drag to rotate · scroll to zoom · double-click to reset framing
                  </p>
                </TabsContent>

                <TabsContent value="2d" className="mt-0">
                  <SkeletalViewer
                    key={`2d-${selected.id}`}
                    molecule={selected.view2d}
                    height={420}
                    ariaLabel={`2D structural diagram of ${selected.name} (${selected.formula})`}
                  />
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    Carbon vertices are implicit. Heteroatoms and explicit hydrogens are labelled.
                  </p>
                </TabsContent>
              </Tabs>
            </div>
          </section>
        </div>
      </Main>
    </div>
  );
}